import { OPEN_LIBRARY_API } from '../config';

// Helper function to retry API calls
const fetchWithRetry = async (url, options = {}, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response;
    } catch (error) {
      console.log(`Attempt ${attempt + 1}/${maxRetries} failed for ${url}: ${error.message}`);
      lastError = error;
      
      if (attempt < maxRetries - 1) {
        // Wait before retrying, with exponential backoff
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
      }
    }
  }
  
  throw lastError;
};

export const getTopBooks = async (page = 1, filter = {}) => {
  const limit = 20;
  const offset = (page - 1) * limit;
  let query = '*';

  if (filter.year) {
    const currentYear = new Date().getFullYear();
    const currentYearMinus1 = currentYear - 1;
    const currentYearMinus2 = currentYear - 2;
    const lastDecadeStart = Math.floor((currentYear - 2) / 10) * 10;
    const lastDecadeMinus10Start = lastDecadeStart - 10;
    const lastDecadeMinus20Start = lastDecadeStart - 20;

    if (filter.year === 'currentYear') {
      query = `${query} AND first_publish_year:${currentYear}`;
    } else if (filter.year === 'currentYearMinus1') {
      query = `${query} AND first_publish_year:${currentYearMinus1}`;
    } else if (filter.year === 'currentYearMinus2') {
      query = `${query} AND first_publish_year:${currentYearMinus2}`;
    } else if (filter.year === 'lastDecade') {
      // Books from the 2020s
      query = `${query} AND first_publish_year:[${lastDecadeStart} TO ${currentYear}]`;
    } else if (filter.year === 'lastDecadeMinus10') {
      // Books from the 2010s
      query = `${query} AND first_publish_year:[${lastDecadeMinus10Start} TO ${lastDecadeStart-1}]`;
    } else if (filter.year === 'lastDecadeMinus20') {
      // Books from the 2000s
      query = `${query} AND first_publish_year:[${lastDecadeMinus20Start} TO ${lastDecadeMinus10Start-1}]`;
    } else if (filter.year === 'older') {
      // Books before 2000
      query = `${query} AND first_publish_year:[0 TO ${lastDecadeMinus20Start-1}]`;
    } else {
      query = `${query} AND first_publish_year:${filter.year}`;
    }
  }
  if (filter.author) {
    query = `${query} AND author_name:"${filter.author}"`;
  }
  if (filter.subject) {
    query = `${query} AND subject:"${filter.subject}"`;
  }

  try {
    const response = await fetchWithRetry(
      `${OPEN_LIBRARY_API}/search.json?q=${encodeURIComponent(query)}&sort=rating&limit=${limit}&offset=${offset}`
    );
    const data = await response.json();
    
    // Process books and fetch ratings for each
    const booksWithRatings = await Promise.all(data.docs.map(async book => {
      let rating = 'loading';
      let subjects = undefined; // Initialize as undefined to indicate loading state
      
      // Extract the work ID for ratings
      if (book.key) {
        const workId = book.key.includes('/works/') 
          ? book.key.split('/works/')[1] 
          : book.key.replace(/^\//, '');
        
        try {
          // Create promises for both API calls
          const fetchRatings = (async () => {
            try {
              const ratingsUrl = `${OPEN_LIBRARY_API}/works/${workId}/ratings.json`;
              
              // Create a promise that rejects after 3 seconds
              const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Ratings fetch timeout')), 3000);
              });
              
              // Race the fetch against the timeout
              const ratingsResponse = await Promise.race([
                fetchWithRetry(ratingsUrl, {}, 2, 500),
                timeoutPromise
              ]);
              
              const ratingsData = await ratingsResponse.json();
              if (ratingsData.summary?.average) {
                return ratingsData.summary.average.toFixed(1);
              }
            } catch (error) {
              console.log(`Could not fetch ratings after retries for book ${book.key}:`, error);
            }
            return 'No rating';
          })();
          
          const fetchSubjects = (async () => {
            try {
              const workDetailsUrl = `${OPEN_LIBRARY_API}/works/${workId}.json`;
              const workResponse = await fetchWithRetry(workDetailsUrl, {}, 3, 500); // Increased retries for subjects
              
              const workData = await workResponse.json();
              if (workData.subjects && workData.subjects.length > 0) {
                // Store subjects in localStorage as a cache
                try {
                  localStorage.setItem(`book_subjects_${workId}`, JSON.stringify(workData.subjects));
                } catch (e) {
                  // Ignore storage errors
                }
                return workData.subjects;
              }
            } catch (error) {
              console.log(`Could not fetch work details after retries for book ${book.key}:`, error);
            }
            return [];
          })();
          
          // Check if we have cached subjects for this book
          let cachedSubjects = [];
          try {
            const cached = localStorage.getItem(`book_subjects_${workId}`);
            if (cached) {
              cachedSubjects = JSON.parse(cached);
            }
          } catch (e) {
            // Ignore storage errors
          }
          
          // Wait for both API calls to complete
          const [ratingResult, subjectsResult] = await Promise.all([fetchRatings, fetchSubjects]);
          
          rating = ratingResult;
          
          // Use the fetched subjects, or fall back to cached subjects if the fetch returned empty
          subjects = subjectsResult.length > 0 ? subjectsResult : (cachedSubjects.length > 0 ? cachedSubjects : []);
          
        } catch (error) {
          console.log(`Could not fetch data for book ${book.key}:`, error);
          rating = 'No rating';
          
          // Try to get subjects from cache even if the main request failed
          try {
            const cached = localStorage.getItem(`book_subjects_${workId}`);
            if (cached) {
              subjects = JSON.parse(cached);
            } else {
              subjects = [];
            }
          } catch (e) {
            subjects = [];
          }
        }
      } else {
        rating = 'No rating';
        subjects = [];
      }
      
      return {
        id: book.key,
        title: book.title,
        author: book.author_name?.[0] || 'Unknown Author',
        year: book.first_publish_year || 'Unknown Year',
        rating: rating,
        coverId: book.cover_i,
        isbn: book.isbn?.[0],
        subject: book.subject?.[0],
        subjects: subjects
      };
    }));
    
    return {
      docs: booksWithRatings,
      total: data.numFound,
      page,
      hasMore: offset + limit < data.numFound
    };
  } catch (error) {
    console.error('Error fetching books after all retries:', error);
    throw error;
  }
};

export const getBookDetails = async (bookId) => {
  try {
    if (!bookId) {
      throw new Error('Book ID is required');
    }
    
    // Clean up the bookId - remove any leading or trailing slashes and spaces
    let formattedBookId = bookId.trim();
    
    // Check if the ID is already in the format "works/OL12345W"
    if (formattedBookId.match(/^works\/OL\d+W$/)) {
      formattedBookId = `/${formattedBookId}`;
    } 
    // Check if the ID is just "OL12345W" without the "works/" prefix
    else if (formattedBookId.match(/^OL\d+W$/)) {
      formattedBookId = `/works/${formattedBookId}`;
    }
    // If it doesn't start with a slash, add one
    else if (!formattedBookId.startsWith('/')) {
      formattedBookId = `/${formattedBookId}`;
    }
    
    console.log('getBookDetails - Original bookId:', bookId);
    console.log('getBookDetails - Formatted bookId:', formattedBookId);
    
    // Fetch basic book data with retry
    const apiUrl = `${OPEN_LIBRARY_API}${formattedBookId}.json`;
    console.log('Fetching book data from:', apiUrl);
    const response = await fetchWithRetry(apiUrl);
    
    const data = await response.json();
    console.log('Book data received:', data);
    
    // Extract the work ID for ratings (without the /works/ prefix)
    let workId = formattedBookId;
    if (workId.includes('/works/')) {
      workId = workId.split('/works/')[1];
    } else if (workId.startsWith('/')) {
      workId = workId.substring(1);
    }
    
    // Fetch ratings if available with retry
    let ratings = null;
    try {
      const ratingsUrl = `${OPEN_LIBRARY_API}/works/${workId}/ratings.json`;
      console.log('Fetching ratings from:', ratingsUrl);
      const ratingsResponse = await fetchWithRetry(ratingsUrl, {}, 2);
      ratings = await ratingsResponse.json();
      console.log('Ratings data received:', ratings);
    } catch (error) {
      console.log('Could not fetch ratings after retries:', error);
    }
    
    // Prepare cover URL
    let coverUrl = null;
    if (data.covers?.[0]) {
      coverUrl = `https://covers.openlibrary.org/b/id/${data.covers[0]}-L.jpg`;
      console.log('Cover URL:', coverUrl);
    }

    // Format the authors data properly
    let authors = [];
    
    console.log('Author data from API:', {
      authors: data.authors,
      author_name: data.author_name,
      author: data.author,
      by_statement: data.by_statement,
      contribution: data.contribution
    });
    
    // First try to get authors from the data.authors field, which is often present but contains references
    if (data.authors && Array.isArray(data.authors)) {
      console.log('Processing data.authors:', data.authors);
      
      // Create an array of promises to fetch author details
      const authorPromises = data.authors.map(async (authorRef) => {
        // If we have a key but no name, fetch the author details
        if (authorRef.author && authorRef.author.key) {
          try {
            const authorUrl = `${OPEN_LIBRARY_API}${authorRef.author.key}.json`;
            console.log('Fetching author data from:', authorUrl);
            const authorResponse = await fetchWithRetry(authorUrl, {}, 2);
            const authorData = await authorResponse.json();
            console.log('Author data received:', authorData);
            return { name: authorData.name || authorData.personal_name || 'Unknown Author' };
          } catch (error) {
            console.error('Error fetching author details:', error);
            return { name: 'Unknown Author' };
          }
        } else if (authorRef.key) {
          try {
            const authorUrl = `${OPEN_LIBRARY_API}${authorRef.key}.json`;
            console.log('Fetching author data from:', authorUrl);
            const authorResponse = await fetchWithRetry(authorUrl, {}, 2);
            const authorData = await authorResponse.json();
            console.log('Author data received:', authorData);
            return { name: authorData.name || authorData.personal_name || 'Unknown Author' };
          } catch (error) {
            console.error('Error fetching author details:', error);
            return { name: 'Unknown Author' };
          }
        } else if (authorRef.name) {
          return { name: authorRef.name };
        } else {
          return { name: 'Unknown Author' };
        }
      });
      
      // Wait for all author requests to complete
      authors = await Promise.all(authorPromises);
    } 
    // Check for author_name field from search results
    else if (data.author_name && Array.isArray(data.author_name)) {
      console.log('Processing data.author_name:', data.author_name);
      authors = data.author_name.map(name => ({ name }));
    }
    // Check for singular author or author_name field
    else if (data.author) {
      console.log('Processing data.author:', data.author);
      authors = [{ name: data.author }];
    }
    // If no authors found, use 'Unknown Author'
    else {
      console.log('No author information found, using default');
      authors = [{ name: 'Unknown Author' }];
    }
    
    // If still no authors, try to extract from other fields
    if (authors.length === 0) {
      console.log('Author array is empty, trying alternative fields');
      const authorName = data.by_statement || data.contribution?.by || 'Unknown Author';
      authors = [{ name: authorName }];
    }
    
    console.log('Final formatted authors:', authors);
    
    // Prepare external links
    const links = [];
    if (data.links) {
      data.links.forEach(link => {
        if (link.url && link.title) {
          links.push({
            url: link.url,
            title: link.title
          });
        }
      });
    }
    
    // Add a link to Open Library
    links.push({
      url: `https://openlibrary.org${formattedBookId}`,
      title: 'View on Open Library'
    });

    // Prepare the final book data object
    const bookData = {
      ...data,
      coverUrl,
      description: data.description?.value || data.description || 'No description available',
      ratings_average: ratings?.summary?.average,
      ratings_count: ratings?.summary?.count,
      authors: authors,
      links
    };
    
    console.log('Final book data:', bookData);
    return bookData;
  } catch (error) {
    console.error('Error fetching book details after all retries:', error);
    throw error;
  }
};

// Predefined categories for books
export const bookCategories = {
  years: [
    { label: 'All Time', value: '' },
    { label: `${new Date().getFullYear()}`, value: 'currentYear' },
    { label: `${new Date().getFullYear() - 1}`, value: 'currentYearMinus1' },
    { label: `${new Date().getFullYear() - 2}`, value: 'currentYearMinus2' },
    { label: `${Math.floor((new Date().getFullYear() - 2) / 10) * 10}s`, value: 'lastDecade' },
    { label: `${Math.floor((new Date().getFullYear() - 2) / 10) * 10 - 10}s`, value: 'lastDecadeMinus10' },
    { label: `${Math.floor((new Date().getFullYear() - 2) / 10) * 10 - 20}s`, value: 'lastDecadeMinus20' },
    { label: 'Older Books', value: 'older' }
  ],
  subjects: [
    { label: 'All Genres', value: '' },
    { label: 'Fiction', value: 'fiction' },
    { label: 'Science Fiction', value: 'science_fiction' },
    { label: 'Fantasy', value: 'fantasy' },
    { label: 'Mystery', value: 'mystery' },
    { label: 'Romance', value: 'romance' },
    { label: 'Biography', value: 'biography' },
    { label: 'History', value: 'history' },
    { label: 'Science', value: 'science' }
  ]
}; 